import Select from 'react-select';
import { useEffect, useState } from 'react';
import { useGetChallengeList } from "../../../queries/challenge";
import { useGetAppDisplayConfig, useGetCurrentChallengeOfTheDay, useSetNewChallengeOfTheDay, useUpdateAppDisplayConfig } from '../../../queries/homepageOrganizer';
import { useForm } from 'react-hook-form';
import { ActionButton } from '../../../components/Forms/ActionButton';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ChallengeOfTheDay = () => {
    const { data: challengeList, isPending: isChallengeListPending } = useGetChallengeList();
    const { data: currentChallengeOfTheDay, isPending: isCurrentChallengeOfTheDayPending } = useGetCurrentChallengeOfTheDay();

    const [challengeOfTheDay, setChallengeOfTheDay] = useState(currentChallengeOfTheDay?.challengeOfTheDay);

    const { mutateAsync: setNewChallengeOfTheDay, isPending: isSetNewChallengeOfTheDayPending } = useSetNewChallengeOfTheDay();

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm();

    useEffect(() => {
        if (currentChallengeOfTheDay) {
            setValue("challengeOfTheDay", currentChallengeOfTheDay._id || null);
            setValue("challengeOfTheDayPoints", currentChallengeOfTheDay.challengeOfTheDayPoints);
            setValue("maxUserAllowed", currentChallengeOfTheDay.maxUserAllowed);
            setChallengeOfTheDay(currentChallengeOfTheDay?._id ? true : false);
        }
    }, [currentChallengeOfTheDay, setValue]);

    const onSubmit = async (data) => {
        if (isSetNewChallengeOfTheDayPending) toast.info("Updating challenge of the day...");
        setNewChallengeOfTheDay({ ...data, active: challengeOfTheDay }, {
            onSuccess: () => {
                toast.dismiss();
                toast.success("Challenge of the day updated successfully");
                if (!challengeOfTheDay) {
                    setValue("challengeOfTheDay", null, { shouldValidate: true });
                    document.querySelector('.select__control').click(); // Open dropdown
                    document.querySelector('.select__clear-indicator')?.click(); // Clear selection
                    setValue("challengeOfTheDayPoints", "");
                    setValue("maxUserAllowed", "");
                }
            }
        });
    }

    if (isChallengeListPending || isCurrentChallengeOfTheDayPending) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="loader">Loading...</div>
            </div>
        );
    }

    return (
        <div className="bg-white shadow-md rounded-lg p-5 mb-5">
            <h2 className="text-xl text-primary font-bold">Challenge of the day</h2>
            <div className='mt-2 flex items-center gap-2'>
                <p className="text-base text-primary font-bold">Show challenge of the day in app</p>
                <div className={`flex justify-end items-center border ${challengeOfTheDay ? `border-primary` : `border-inactive`} rounded-md h-full min-h-8 min-w-20`}>
                    <label className="inline-flex items-center me-5 cursor-pointer h-full">
                        <span className={`ms-3 text-base ${challengeOfTheDay ? `text-primary` : 'text-inactive'} font-bold dark:text-gray-300 mr-2`}>{challengeOfTheDay ? 'Yes' : 'No'}</span>
                        <input type="checkbox" name={'challengeOfTheDay'} value={challengeOfTheDay} className="sr-only peer" onChange={() => setChallengeOfTheDay(!challengeOfTheDay)} checked={challengeOfTheDay} />
                        <div className="relative w-8 h-4 bg-white border-black ring-1 ring-inactive rounded-full peer dark:bg-white peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.1 after:start-[0px] after:bg-inactive after:border-inactive peer-checked:after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-navBtn peer-checked:ring-primary peer-checked:bg-primary"></div>
                    </label>
                </div>
            </div>
            <div className='mt-2 flex items-center gap-2'>
                <p className="text-base text-primary font-bold">Current challenge of the day in app:</p>
                <p className="text-base font-semibold">{currentChallengeOfTheDay?.name ?? '-'}</p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className='flex gap-3'>
                    {/* New challenge of the day selection */}
                    <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-2">
                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                            Select new challenge of the day
                        </label>
                        <Select
                            options={challengeList?.filter(challenge => challenge.active).map((challenge) => ({
                                value: challenge._id,
                                label: challenge.name,
                            }))}
                            isLoading={isChallengeListPending}
                            placeholder="Select a challenge of the day"
                            classNamePrefix="select"
                            className='mt-1 text-base'
                            isClearable
                            isSearchable
                            defaultValue={{
                                value: currentChallengeOfTheDay && currentChallengeOfTheDay?._id,
                                label: currentChallengeOfTheDay && currentChallengeOfTheDay?.name,
                            }}
                            name='challengeOfTheDay'
                            onChange={(selectedOption) => {
                                setValue("challengeOfTheDay", selectedOption?.value || null);
                                setChallengeOfTheDay(true);
                            }}
                            noOptionsMessage={() => "No challenges available"}
                            styles={{
                                control: (base, state) => ({
                                    ...base,
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${errors.challengeOfTheDay ? 'red' : 'rgba(224, 230, 237, 1)'}`,
                                    borderRadius: '0.375rem',
                                    boxShadow: state.isFocused ? (errors.challengeOfTheDay ? '0 0 0 1px red' : '0 0 0 1px #16ad3c') : 'none',
                                    padding: '0.15rem',
                                    ":hover": {
                                        borderColor: errors.challengeOfTheDay ? 'red' : '#16ad3c',
                                    },
                                }),
                                menu: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                }),
                                option: (base, { isSelected }) => ({
                                    ...base,
                                    backgroundColor: isSelected ? '#16ad3c' : 'white',
                                    color: isSelected ? 'white' : 'black',
                                    ":active": {
                                        backgroundColor: isSelected ? '#16ad3c' : 'rgba(0, 0, 0, 0.1)',
                                    },
                                    ":hover": {
                                        backgroundColor: isSelected ? '#16ad3c' : 'rgba(52, 199, 89, 0.3)',
                                    },
                                }),
                                singleValue: (base) => ({
                                    ...base,
                                    color: 'black',
                                }),
                                placeholder: (base) => ({
                                    ...base,
                                    color: 'gray',
                                }),
                                dropdownIndicator: (base) => ({
                                    ...base,
                                    color: 'gray',
                                }),
                                indicatorSeparator: (base) => ({
                                    ...base,
                                    display: 'none',
                                }),
                                clearIndicator: (base) => ({
                                    ...base,
                                    color: 'gray',
                                }),
                                valueContainer: (base) => ({
                                    ...base,
                                    padding: '0.25rem',
                                }),
                                input: (base) => ({
                                    ...base,
                                    color: 'black',
                                }),
                                menuList: (base) => ({
                                    ...base,
                                    maxHeight: '300px',
                                }),
                            }}
                        />
                        {errors.challengeOfTheDay && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.challengeOfTheDay.message || "This field is required"}
                            </p>
                        )}
                    </div>
                    {/* Points */}
                    <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-2">
                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                            Points for the challenge of the day
                        </label>
                        <div className={`flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border ${errors.points ? 'border-red' : 'border-border'} text-slate-400`}>
                            <input
                                type="number"
                                name="challengeOfTheDayPoints"
                                id="challengeOfTheDayPoints"
                                {...register("challengeOfTheDayPoints", { required: "Points are required" })}
                                placeholder="Points for the challenge of the day"
                                className={`flex-1 shrink self-stretch my-auto basis-0 outline-none px-4 py-2.5 rounded-md text-base`}
                                min={0}
                            />
                        </div>
                        {
                            errors.points && (
                                <p className="text-red text-sm mt-1">
                                    {errors.points.message || "This field is required"}
                                </p>
                            )
                        }
                    </div>
                    {/* Max User Allowed */}
                    <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-2">
                        <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">
                            Maximum users allowed
                        </label>
                        <div className={`flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border ${errors.maxUserAllowed ? 'border-red' : 'border-border'} text-slate-400`}>
                            <input
                                type="number"
                                name="maxUserAllowed"
                                id="maxUserAllowed"
                                {...register("maxUserAllowed", { 
                                    required: "Maximum users allowed is required",
                                    min: { value: 1, message: "Must be at least 1" }
                                })}
                                placeholder="Maximum number of users allowed"
                                className={`flex-1 shrink self-stretch my-auto basis-0 outline-none px-4 py-2.5 rounded-md text-base`}
                                min={1}
                            />
                        </div>
                        {
                            errors.maxUserAllowed && (
                                <p className="text-red text-sm mt-1">
                                    {errors.maxUserAllowed.message || "This field is required"}
                                </p>
                            )
                        }
                    </div>
                </div>
                <div className='flex justify-end mt-5'>
                    <ActionButton
                        text={'Submit'}
                        variant={'submit'}
                    />
                </div>
            </form>
        </div>
    )
}

export default ChallengeOfTheDay